import {
  ArrivalTimeOptionCodeType,
  BookDateCode,
  BookTimeCode,
  BusStopEventCode,
  NaveMapPolyLineStyle,
  WeekdayCode,
  WeekdayCodeName,
} from "@/types";
import { IBusStop } from "../busStop";
import { LineTypeCode, LineTypeName } from "@/types";
import { BookIntervalCode } from "@/types";

export interface ILineInfoData {
  readonly id?: string;
  readonly lineNum: string;
  readonly routeName: string;
  readonly lineNameDisplayText: string;
  readonly campusCode: string;
  readonly campusName: string;
  readonly lineDirectionCode: string;
  readonly lineDirectionName: string;
  readonly lineDistrictCode: string;
  readonly lineDistrictName: string;
  readonly turns: Array<ITurn>;
  readonly schedules: Array<string>;
  readonly isBusRunning?: boolean;
  readonly lineTypeCode: LineTypeCode;
  readonly lineTypeName: LineTypeName;
  readonly district: string;
  readonly dailyBookInterval?: IBookInterval;
  readonly monthlyBookInterval?: IBookInterval;
  readonly detourBeginDtm?: string;
  readonly detourEndDtm?: string;
  readonly detourReason?: string;
}

export interface IPath {
  latitude: number;
  longitude: number;
}

export interface ILineSet {
  lineSetCodeUid: string;
  name: string;
  displayText: string;
  isToday: boolean;
}

export interface IPathGroupByPeriod {
  lineTurnUid: string;
  useBeginDtm: string;
  useEndDtm?: string;
  path: IPath[];
}

export interface IBusStopGroupByPeriod {
  useBeginDtm: string;
  useEndDtm?: string;
  busStops: IBusStop[];
}

export interface ITurn {
  readonly id: string;
  readonly name: string;
  readonly turnNum: number;
  readonly lineTimeCode?: string;
  readonly lineTimeName?: string;
  readonly busStops: Array<IBusStop>;
  readonly firstStop: IBusStop;
  readonly lastStop: IBusStop;
  readonly lineFare: ILineFare;
  readonly lineSets: ILineSet[];
  readonly earlyDriveInfos?: IEarlyDriveData[]; // 요일별 조기운행 정보
  /** 우회 경로 */
  readonly detourPaths?: IPath[];
  readonly bookYn: boolean; // 예약 여부
  readonly buses?: IBus[];
  readonly bookIntervals?: IBookInterval[];
  /** 사용시작일시 */
  readonly useBeginDtm: string;
  /** 사용종료일시 */
  readonly useEndDtm?: string;
  /** 기간별로 그룹화된 정류장 목록 */
  readonly busStopGroupsByPeriod?: IBusStopGroupByPeriod[];
  /** 기간별로 그룹화된 경로 목록 */
  readonly pathGroupsByPeriod?: IPathGroupByPeriod[];
  /** 구버전 경로 데이터, pathGroupsByPeriod 가 없으면 paths 사용 바람 */
  readonly paths?: IPath[];
}

export interface IEarlyDriveData {
  readonly weekdayCode: WeekdayCode;
  readonly weekdayName: WeekdayCodeName;
  readonly earlyMinute: number;
}

export interface ILineType {
  readonly lineTypeCode: string; // 노선 유형 코드. LRT-FORWAR 목적지행, LRT-BACKWA 복귀행
  readonly lineTypeName: string;
  readonly displayOrder: number;
}

export interface ILineDistrict {
  readonly district: string;
}

export interface IBus {
  readonly allocUid: string;
  readonly busOrder: number;
  readonly carNo?: string;
  readonly vendorName?: string;
  readonly seatCount: number;
  readonly bookCount: number;
  readonly boardCount: number;
  readonly remainCount: number;
  readonly allocStatusCode: string;
  readonly busImages?: IFile[];
}

export interface IFile {
  fileUid: string;
  fileName?: string;
  fileSubTypeCode?: string;
  fileSubTypeCodeName?: string;
  fileUrl?: string;
}

export interface IDownloadedFile {
  file: Blob;
}

export interface ILineInfo extends ILineInfoData {
  validate(data: ILineInfoData): void;
}

export interface IBusLocation {
  allocUid: string;
  lineTurnUid: string;
  lineRouteUid: string;
  lineTypeCode: string;
  totalSeatsCount: number;
  remainingSeatsCount: number;
  boardingCount: number;
  busOrder: number; // 호차
  plateNumber: string;
  driveDate: string;
  collectedTime: string;
  driverEventCode?: string;
  driverEventName?: string;
  allocStatusCode?: string;
  lat: number;
  lng: number;
  /** 방위각 */
  bearing?: number;
  busStopEvents: IBusStopEvent[];
  nextStopName: string;
}

export interface IBusStopEvent {
  /** 정류장 식별자 */
  uid: string;
  /** 정류장 이름 */
  name: string;
  /** 정류장 단축이름 */
  shortName: string;
  /** 정류장 위도 */
  lat: number;
  /** 정류장 경도 */
  lng: number;
  /** 방위각 */
  bearing?: number;
  /** 정류장 도착 예정 시각 (조기 출발 고려 안함) */
  arrivalTime: string;
  /** 정류장 출발 예정 시각 (조기 출발 고려 안함) */
  departureTime: string;
  minutes: number;
  /** 이벤트 수집 일시 (2022-05-02T16:10:10.828+09:00) */
  collectedTs: string;
  /** 정류장 이벤트. Enter:진입, Arrive:도착, Leave:진출 */
  busStopEventCode: BusStopEventCode;
  /** 이전 정류장과의 분단위 간격 */
  prevIntervalMin: number;
}

export interface ILineFareInfo {
  fareAmount: number;
  bookIntervalCode: BookIntervalCode; // BDT-MONTH
  name?: string;
  code?: string;
  displayOrder?: number;
}

export interface ILineFare {
  dailyFare: number | undefined;
  monthlyFare: number | undefined;
}

export interface IBookInterval {
  bookIntervalCode: BookIntervalCode; // BDT-MONTH
  bookIntervalName: string; // 월
  seatsSelectable?: boolean;
  lineTurnUid?: string; // 202106240527011411553019998LNT",
  lineRouteNo?: string; // 당산B",
  lineRouteName?: string; // 당산B",
  lineTypeCode?: string; // LRT-BACKWA",
  bookPointCode?: string; // BKP-NORMAL",
  bookBeginDateCode: BookDateCode; // BDC-DAY",
  bookBeginYear: number; //  0,
  bookBeginMonth: number; //  -1,
  bookBeginWeek?: number; // -1
  bookBeginWeekdayCode?: string; // WKD-FRI
  bookBeginWeekdayName?: string; // 금요일
  bookBeginDay: number; //  17,
  bookBeginTimeCode: BookTimeCode; // BTC-FIXED",
  bookBeginHour: number; //  10,
  bookBeginMin: number; //  0,
  bookEndDateCode: BookDateCode; // BDC-BEGIN",
  bookEndYear: number; //  0,
  bookEndMonth: number; //  0,
  bookEndDay: number; //  0,
  bookEndTimeCode: BookTimeCode; // BTC-BEGIN",
  bookEndHour: number; //  0,
  bookEndMin: number; //  -10,
  bookCancelDateCode: BookDateCode; // BDC-BEGIN",
  bookCancelYear: number; //  0,
  bookCancelMonth: number; //  0,
  bookCancelDay: number; //  0,
  bookCancelTimeCode: BookTimeCode; // BTC-FIXED",
  bookCancelHour: number; //  5,
  bookCancelMin: number; //  0,
  displayOrder?: number; //  0
}

export interface IArrivalTimeOption {
  name: string;
  type: ArrivalTimeOptionCodeType;
}

export const NAVERMAP_POLYLINE_STYLES = [
  NaveMapPolyLineStyle.SOLID,
  NaveMapPolyLineStyle.SHORT_DASH,
  NaveMapPolyLineStyle.SHORT_DOT,
] as const;
